import { defineStore } from "pinia";
import GlobalEventBus from "../EventBus/GlobalEventBus";
import Axios from "../composables/Axios";
import ApiService from "../composables/ApiService";
import { AxiosResponse } from "axios";
import { Customer } from "@daalder/types";
import { useGA4 } from "../composables/GA4";
import { isEqual } from "lodash-es";
import Cookies from "js-cookie";
import { type Dayjs } from "dayjs";

export type RootState = {
    token: string;
    customer: Customer;
    isAuthenticated: boolean;
    isInitialized: boolean;
};

const apiService = new ApiService();

const { trackGALogin, trackGASignUp } = useGA4();

export const useCustomerStore = defineStore({
    id: "customer",
    state: (): RootState => {
        return {
            token: Cookies.get("daalder-api_customer_token"),
            customer: null,
            isAuthenticated: Cookies.get("daalder-is_authenticated") == "1",
            isInitialized: false,
        };
    },
    getters: {
        isGuest: (state): boolean => {
            return state.customer ? state.customer.state_id == 1 : true;
        },
        isLoggedIn: (state): boolean => {
            return !!state.customer;
        },
        areAddressesDifferent: (state): boolean => {
            if (!state.customer) {
                return false;
            }

            const deliveryAddress = state.customer?.primaryDeliveryAddress;
            const invoiceAddress = state.customer?.primaryInvoiceAddress;

            return !isEqual(deliveryAddress, invoiceAddress);
        },
    },
    actions: {
        async init() {
            if (!this.token) {
                await this.logoutAndFetchAnonymousToken();
            } else {
                // TODO: Get rid of this method call
                await this.runLegacyActions({
                    customer_token: this.token,
                    is_guest: this.isGuest,
                    is_authenticated: this.isAuthenticated,
                });
            }

            await this.fetchCustomer();

            this.isInitialized = true;
        },
        // This method doesn't delete a token on the API, but the 'logout' part is included to make it very apparent
        // that this method un-authenticates the customer, in case they are logged in.
        async logoutAndFetchAnonymousToken() {
            return await Axios.getInstance()
                .get(window.ajax_object.daalder_ajax_url, {
                    params: { action: "get_customer_token" },
                })
                .then((response: any) => {
                    let data = response.data.data;
                    this.token = data.customer_token;
                    this.isAuthenticated = data.is_authenticated;

                    // TODO: Get rid of this line
                    this.runLegacyActions(data);

                    return response;
                })
                .catch((error: any) => {
                    throw error;
                });
        },

        async fetchCustomer() {
            if (this.isAuthenticated) {
                return await apiService
                    .get("v2/customers/current?includes=company,primaryDeliveryAddress,primaryInvoiceAddress")
                    .then((response) => {
                        this.customer = response.data.data;

                        // TODO: Get rid of these two lines
                        window.cache.customer = this.customer;
                        GlobalEventBus.emit("customerChanged", this.customer);

                        return response;
                    })
                    .catch((error) => {
                        throw error;
                    });
            }
        },

        /**
         * @todo: postData should not be 'any' but be strictly defined. It could even be a param per data piece.
         */
        async updateCustomer(postData: any) {
            return await apiService
                .put("v2/customers", postData)
                .then(async (response) => {
                    // If the customer wasn't authenticated before and the postData included a password
                    if (this.isAuthenticated && postData?.password) {
                        // TODO: This needs a comment or slight refactor to clarify the 0/1 mapping
                        // The customer is now registered
                        let cookieValue = ((response.data?.data?.state_id ?? 1) === 1 ? 1 : 0).toString();
                        Cookies.set("daalder-is_anonymous", cookieValue, {
                            expires: 365,
                        });
                    }

                    // Fetch customer again (don't use the response here, there's includes missing)
                    await this.fetchCustomer();

                    return response;
                })
                .catch((error) => {
                    throw error;
                });
        },

        /**
         * @todo Type should be fixed
         */
        async updateBirthdate(date: string) {
            return await apiService
                .put("v2/customers", { birthday: date })
                .then(async (response) => {
                    this.customer.birthday = response.data.data.birthday;

                    return response;
                })
                .catch((error) => {
                    throw error;
                });
        },

        /**
         * @todo: postData should not be 'any' but be strictly defined. It could even be two params (username/password).
         * @todo: This should not allow a custom url but instead just use the ApiHelper to do an auth call to the Daalder API
         */
        async loginCustomer(postData: any, customUrl: string): Promise<AxiosResponse | void> {
            return await Axios.getInstance()
                .post(customUrl, postData)
                .then(async (response: any) => {
                    this.isAuthenticated = true;

                    // Update daalder API customer token for login  without refresh,
                    // otherwise fetchCustomer will fail
                    this.token = response.data.token;
                    (Axios.getInstance().defaults.headers as Record<string, any>).common["X-Daalder-Customer-Token"] =
                        this.token;

                    // TODO: Get rid of this line
                    // This cookie is used in the PHP side of WordPress, that's why it's updated here.
                    Cookies.set("daalder-is_authenticated", "1", { expires: 365 });

                    const fetchResponse = await this.fetchCustomer();

                    if (fetchResponse.statusText === "OK" || fetchResponse.status === 200) {
                        // TODO: Get rid of this line
                        GlobalEventBus.emit("loggedIn");

                        trackGALogin();
                    }

                    return response;
                })
                .catch((error: any) => {
                    throw error;
                });
        },

        /**
         * @todo: postData should not be 'any' but be strictly defined. It could even be separate params (one per data piece).
         * @todo: This should not allow a custom url but instead just use the ApiHelper to do an auth call to the Daalder API
         */
        async registerCustomer(postData: any, customUrl: string = null) {
            let url = customUrl ?? window.ajax_object.base_url + "/register";

            return await Axios.getInstance()
                .post(url, postData)
                .then(async (response: any) => {
                    this.isAuthenticated = true;

                    // Update daalder API customer token for register without refresh,
                    // otherwise fetchCustomer will fail
                    this.token = Cookies.get("daalder-api_customer_token");

                    (Axios.getInstance().defaults.headers as Record<string, any>).common["X-Daalder-Customer-Token"] =
                        this.token;

                    // This cookie is used in the PHP side of WordPress, that's why it's updated here.
                    Cookies.set("daalder-is_authenticated", "1", { expires: 365 });

                    const fetchResponse = await this.fetchCustomer();

                    if (fetchResponse.statusText === "OK" || fetchResponse.status === 200) {
                        GlobalEventBus.emit("loggedIn");
                        trackGASignUp();
                    }

                    return response;
                })
                .catch((error: any) => {
                    throw error;
                });
        },

        /**
         * @todo: Get rid of this
         */
        async runLegacyActions(data: { customer_token: string; is_guest: boolean; is_authenticated: boolean }) {
            window.cache.daalder = data;

            (Axios.getInstance().defaults.headers as Record<string, any>).common["X-Daalder-Customer-Token"] =
                this.token;
            GlobalEventBus.emit("customerInfoUpdated", data);
        },
    },
});
